// Stufe 4 (LEA-379) — the CUSTOM tripwire checkout. Janik 2026-07-09: the hosted/
// embedded Checkout renders the €49/3-day price literally as "€49 every 3 days" (Om
// finding P1), so we render OUR OWN honest order summary + a Stripe Payment Element
// inline.
//
// Mission #8 (Janik 2026-07-10): ONE surface, no in-between click. Name/Firma + Land +
// Rechnungsadresse + optional VAT-ID auf EINER Fläche, darunter das Payment Element
// (Prefill via defaultValues; es sammelt zusätzlich line2/state selbst).
//
// §14-Korrektur (Janik 2026-07-11): #8 hatte Straße/PLZ/Ort ganz entfernt und nur { country }
// an den Mint geschickt → die 49-€-Rechnung trug KEINE vollständige Empfängeradresse (die vom
// Payment Element gesammelte Adresse landet nur auf der Zahlungsmethode, nicht auf dem
// Customer/der Rechnung). Jetzt erfassen wir Straße/PLZ/Ort WIEDER in unserem Formular und
// schreiben sie VOR dem Mint an den Customer → §14-UStG-konforme Rechnung. Der Mint feuert
// erst, wenn Name + volle Adresse stehen; das Element wird via defaultValues vorbefüllt.
//
//   mint — POST name + full address (+ optional VAT-ID) to
//   /checkout/prism-tripwire/payment-element → the backend sets the customer address and
//   mints a `default_incomplete` €49/3-day sub, returning its PaymentIntent client secret
//   (+ publishable key). A 409 `element_mode_off` (flag dark) OR any hard init failure
//   calls onFallback() → the caller shows the embedded/redirect CTA (never dead-end).
//   pay — mount the Payment Element on that client secret and confirmPayment(); on success
//   Stripe redirects to /billing/success, where the webhook (incomplete→active) grants +50,
//   attaches the €290 schedule and seeds — the same money path.
//
// Vanilla @stripe/stripe-js (loadStripe + elements + .mount) to match PrismEmbeddedCheckout.

import { t, useLang } from '@cortex/i18n';
import {
  type Stripe,
  type StripeElements,
  type StripePaymentElement,
  loadStripe,
} from '@stripe/stripe-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { pushDataLayer } from '../../lib/analytics';
import {
  BillingError,
  type PrismElementBilling,
  probePrismElementMode,
  startPrismTripwirePaymentElement,
} from '../../lib/billing';
import { loadBillingSnapshot, saveBillingSnapshot } from './funnelPersistence';
import { sendPrismEvent } from './prismEvents';

const stripeCache = new Map<string, ReturnType<typeof loadStripe>>();
function loadStripeCached(pk: string) {
  let p = stripeCache.get(pk);
  if (!p) {
    p = loadStripe(pk);
    stripeCache.set(pk, p);
  }
  return p;
}

// Janik 11.07.: „alle Länder die es gibt" statt kuratierter DACH+EU-Set. Labels werden
// zur Laufzeit über Intl.DisplayNames in der UI-Sprache lokalisiert (früher fixe deutsche
// Labels → NL-User sah „Niederlande" statt „Nederland"). DACH bleibt oben angepinnt
// (der Funnel ist DACH-primär), der Rest alphabetisch nach lokalisiertem Namen.
const PRIORITY_COUNTRIES = ['DE', 'AT', 'CH'];
// ISO 3166-1 alpha-2 (ohne die 3 Priority-Codes, die separat oben stehen).
const OTHER_COUNTRY_CODES = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AL',
  'AM',
  'AO',
  'AR',
  'AU',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BN',
  'BO',
  'BR',
  'BS',
  'BT',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CD',
  'CG',
  'CI',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CY',
  'CZ',
  'DK',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GH',
  'GM',
  'GN',
  'GQ',
  'GR',
  'GT',
  'GW',
  'GY',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IN',
  'IQ',
  'IS',
  'IT',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MG',
  'MK',
  'ML',
  'MM',
  'MN',
  'MR',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NE',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PG',
  'PH',
  'PK',
  'PL',
  'PT',
  'PY',
  'QA',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SC',
  'SD',
  'SE',
  'SG',
  'SI',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SY',
  'SZ',
  'TD',
  'TG',
  'TH',
  'TJ',
  'TL',
  'TM',
  'TN',
  'TR',
  'TT',
  'TZ',
  'UA',
  'UG',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VN',
  'YE',
  'ZA',
  'ZM',
  'ZW',
];

// Land-Default aus der UI-Sprache (es gibt kein Land im Locale-Modell). en → DE bewahrt
// das frühere Hardcode-'DE' (der Funnel ist DACH-primär). Bestehender State (User-Auswahl)
// hat Vorrang, weil es keinen Step-Reset mehr gibt, der ihn überschreiben würde.
const LOCALE_COUNTRY: Record<string, string> = {
  de: 'DE',
  fr: 'FR',
  es: 'ES',
  it: 'IT',
  nl: 'NL',
  pl: 'PL',
  en: 'DE',
};

// Map the selected country to the Stripe tax-id type so a legit CH/GB id isn't dropped
// under eu_vat. null → no supported VAT type → the VAT-ID field is hidden and no id is sent.
// Janik 11.07.: seit der Länderliste ALLE Länder enthält, MUSS eu_vat auf echte EU-Mitglieder
// beschränkt sein (vorher „alles außer CH/GB/US = eu_vat" → eine NO-/AU-/JP-USt-IdNr. wäre
// als eu_vat an Stripe gegangen und abgelehnt worden).
const EU_VAT_COUNTRIES = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
]);
function vatTypeForCountry(country: string): string | null {
  if (country === 'CH') return 'ch_vat';
  if (country === 'GB') return 'gb_vat';
  if (EU_VAT_COUNTRIES.has(country)) return 'eu_vat';
  return null;
}
// Länder-spezifisches Beispiel-Format für den USt-IdNr.-Platzhalter (Janik 11.07.: der
// Platzhalter soll zum gewählten Land passen, nicht überall „DE123456789" zeigen). Nur für
// Länder mit VAT-ID-Feld nötig (EU + CH + GB); für den Rest ist das Feld ausgeblendet.
const VAT_PLACEHOLDER: Record<string, string> = {
  DE: 'DE123456789',
  AT: 'ATU12345678',
  BE: 'BE0123456789',
  BG: 'BG123456789',
  HR: 'HR12345678901',
  CY: 'CY12345678X',
  CZ: 'CZ12345678',
  DK: 'DK12345678',
  EE: 'EE123456789',
  FI: 'FI12345678',
  FR: 'FR12345678901',
  GR: 'EL123456789',
  HU: 'HU12345678',
  IE: 'IE1234567X',
  IT: 'IT12345678901',
  LV: 'LV12345678901',
  LT: 'LT123456789',
  LU: 'LU12345678',
  MT: 'MT12345678',
  NL: 'NL123456789B01',
  PL: 'PL1234567890',
  PT: 'PT123456789',
  RO: 'RO1234567890',
  SK: 'SK1234567890',
  SI: 'SI12345678',
  ES: 'ESX1234567X',
  SE: 'SE123456789012',
  CH: 'CHE-123.456.789 MWST',
  GB: 'GB123456789',
};
// Länder, deren Rechnungsadresse ein Bundesland/Provinz braucht (Adress-Vollständigkeit +
// US-Sales-Tax via automatic_tax). Janik 11.07. (B: alle Länder + State-Feld nur wo nötig).
const STATE_COUNTRIES = new Set(['US', 'CA', 'AU', 'BR', 'IN', 'MX']);

// Bilingual DE/EN via useLang() — the funnel's real languages — WITHOUT touching the
// 7-locale i18n gate. 16.10.2026: die Abo-Zeile der Übersicht war fest Englisch („€49 for 3
// days, then €290/mo — cancel anytime.“), auch für deutsche Kunden; jetzt der Abo-Satz aus
// prism.abo.line in allen 7 Sprachen, identisch mit Ergebnisseite und Angebotsseite.
const STR = {
  de: {
    dueToday: 'Heute fällig',
    name: 'Name / Firma',
    country: 'Land',
    vat: 'USt-IdNr.',
    vatOpt: '(optional, B2B)',
    vatToggle: 'USt-IdNr. hinzufügen (optional, B2B)',
    vatPlaceholder: 'DE123456789',
    billing: 'Rechnungsadresse',
    street: 'Straße & Nr.',
    postal: 'PLZ',
    city: 'Stadt',
    state: 'Bundesland / Region',
    // O1/§312j (Button-Lösung): Preis MUSS auf den Kauf-Button — konsistent zur
    // Paywall ('Alle {n} freischalten — 49 €'), signalisiert die Zahlungspflicht.
    // {n} = gelieferte Firmen dieses Laufs (Prop `leads`, 15.10.2026), nicht pauschal 50.
    pay: '{n} passende Firmen freischalten — 49 € zahlen',
    paying: 'Zahlung wird verarbeitet…',
    errPay: 'Zahlung fehlgeschlagen.',
    errField: 'Zahlungsfeld konnte nicht geladen werden.',
    enterName: 'Fülle Name & Rechnungsadresse aus, um die Zahlung zu laden.',
    // DEPRECATED nach Mission #8 (Ein-Flächen-Checkout): PLZ/Stadt/Straße sammelt jetzt
    // das Payment Element selbst; kein Step-1 mehr → 'Weiter zur Zahlung'/'Zurück' entfallen.
    // Bewusst als Rollback-Referenz behalten, nicht mehr verdrahtet:
    // postal: 'PLZ', city: 'Stadt', street: 'Straße & Nr.',
    // continue: 'Weiter zur Zahlung', continuing: 'Einen Moment…', back: 'Zurück',
  },
  en: {
    dueToday: 'Due today',
    name: 'Name / Company',
    country: 'Country',
    vat: 'VAT ID',
    vatOpt: '(optional, B2B)',
    vatToggle: 'Add VAT ID (optional, B2B)',
    vatPlaceholder: 'DE123456789',
    billing: 'Billing address',
    street: 'Street & no.',
    postal: 'Postal code',
    city: 'City',
    state: 'State / Province',
    pay: 'Unlock {n} matching companies — pay €49',
    paying: 'Processing payment…',
    errPay: 'Payment failed.',
    errField: "Couldn't load the payment field.",
    enterName: 'Fill in your name & billing address to load payment.',
    // DEPRECATED (siehe DE): postal: 'Postal code', city: 'City', street: 'Street & no.',
    // continue: 'Continue to payment', continuing: 'One moment…', back: 'Back',
  },
  // #14 (Janik 10.07.): 5 restliche Funnel-Sprachen — vorher sahen FR/IT/ES/NL/PL
  // englische Zahlformular-Labels. §312j-CTA (Preis + Zahlsignal) je Sprache.
  fr: {
    dueToday: "À payer aujourd'hui",
    name: 'Nom / Société',
    country: 'Pays',
    vat: 'N° TVA',
    vatOpt: '(facultatif, B2B)',
    vatToggle: 'Ajouter un n° TVA (facultatif, B2B)',
    vatPlaceholder: 'DE123456789',
    billing: 'Adresse de facturation',
    street: 'Rue & n°',
    postal: 'Code postal',
    city: 'Ville',
    state: 'État / Province',
    pay: 'Débloquer {n} entreprises adaptées — payer 49 €',
    paying: 'Traitement du paiement…',
    errPay: 'Le paiement a échoué.',
    errField: 'Impossible de charger le champ de paiement.',
    enterName: 'Renseigne ton nom & ton adresse de facturation pour charger le paiement.',
  },
  it: {
    dueToday: 'Dovuto oggi',
    name: 'Nome / Azienda',
    country: 'Paese',
    vat: 'Partita IVA',
    vatOpt: '(facoltativo, B2B)',
    vatToggle: 'Aggiungi partita IVA (facoltativo, B2B)',
    vatPlaceholder: 'DE123456789',
    billing: 'Indirizzo di fatturazione',
    street: 'Via e n.',
    postal: 'CAP',
    city: 'Città',
    state: 'Stato / Provincia',
    pay: 'Sblocca {n} aziende adatte — paga 49 €',
    paying: 'Elaborazione del pagamento…',
    errPay: 'Pagamento non riuscito.',
    errField: 'Impossibile caricare il campo di pagamento.',
    enterName: 'Inserisci nome e indirizzo di fatturazione per caricare il pagamento.',
  },
  es: {
    dueToday: 'A pagar hoy',
    name: 'Nombre / Empresa',
    country: 'País',
    vat: 'NIF-IVA',
    vatOpt: '(opcional, B2B)',
    vatToggle: 'Añadir NIF-IVA (opcional, B2B)',
    vatPlaceholder: 'DE123456789',
    billing: 'Dirección de facturación',
    street: 'Calle y n.º',
    postal: 'Código postal',
    city: 'Ciudad',
    state: 'Estado / Provincia',
    pay: 'Desbloquear {n} empresas adecuadas — pagar 49 €',
    paying: 'Procesando el pago…',
    errPay: 'El pago ha fallado.',
    errField: 'No se pudo cargar el campo de pago.',
    enterName: 'Completa tu nombre y dirección de facturación para cargar el pago.',
  },
  nl: {
    dueToday: 'Vandaag te betalen',
    name: 'Naam / Bedrijf',
    country: 'Land',
    vat: 'Btw-nummer',
    vatOpt: '(optioneel, B2B)',
    vatToggle: 'Btw-nummer toevoegen (optioneel, B2B)',
    vatPlaceholder: 'DE123456789',
    billing: 'Factuuradres',
    street: 'Straat & nr.',
    postal: 'Postcode',
    city: 'Plaats',
    state: 'Staat / Provincie',
    pay: '{n} passende bedrijven ontgrendelen — 49 € betalen',
    paying: 'Betaling wordt verwerkt…',
    errPay: 'Betaling mislukt.',
    errField: 'Kon het betaalveld niet laden.',
    enterName: 'Vul je naam & factuuradres in om de betaling te laden.',
  },
  pl: {
    dueToday: 'Do zapłaty dziś',
    name: 'Imię / Firma',
    country: 'Kraj',
    vat: 'NIP UE',
    vatOpt: '(opcjonalnie, B2B)',
    vatToggle: 'Dodaj NIP UE (opcjonalnie, B2B)',
    vatPlaceholder: 'DE123456789',
    billing: 'Adres rozliczeniowy',
    street: 'Ulica i nr',
    postal: 'Kod pocztowy',
    city: 'Miasto',
    state: 'Region / Stan',
    pay: 'Odblokuj pasujące firmy ({n}) — zapłać 49 €',
    paying: 'Przetwarzanie płatności…',
    errPay: 'Płatność nie powiodła się.',
    errField: 'Nie udało się załadować pola płatności.',
    enterName: 'Podaj imię i adres rozliczeniowy, aby załadować płatność.',
  },
} as const;

// LEA-429: tax labels per funnel language. Net (exclusive) B2B pricing → the summary shows
// the REAL charged amount with a tax note. `netVat` = generic "plus VAT" hint before the
// amount is known; `ust` = the VAT abbreviation in the breakdown. "Reverse Charge" is a
// standard cross-EU invoice term, kept verbatim.
const TAX_STR = {
  de: { netVat: 'zzgl. USt.', ust: 'USt.' },
  en: { netVat: 'plus VAT', ust: 'VAT' },
  fr: { netVat: 'TVA en sus', ust: 'TVA' },
  it: { netVat: 'IVA escl.', ust: 'IVA' },
  es: { netVat: 'más IVA', ust: 'IVA' },
  nl: { netVat: 'excl. btw', ust: 'btw' },
  pl: { netVat: 'plus VAT', ust: 'VAT' },
} as const;
const REVERSE_CHARGE_LABEL = 'Reverse Charge';

// Money format from cents, matching the app's currency convention (PricingModal.formatEUR,
// Account.jsx BillingPanel): locale-formatted number with the symbol AFTER it.
// Om QA 13.07: Intl's `style:'currency'` renders "€49.00" (symbol first) under an English UI,
// which clashed with the Billing tab's "290,00 €" on the same screen. Same locale mapping as
// BillingPanel's dateLocale (en → en-US, everything else → de-DE) so both surfaces agree.
//   de/fr/… → "58,31 €"   ·   en → "58.31 €"
function fmtMoney(cents: number, lang: string, currency = 'EUR'): string {
  const locale = lang === 'en' ? 'en-US' : 'de-DE';
  const symbol = currency.toUpperCase() === 'EUR' ? '€' : currency.toUpperCase();
  try {
    const n = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
    return `${n} ${symbol}`;
  } catch {
    return `${(cents / 100).toFixed(2)} ${symbol}`;
  }
}

type AmountInfo = {
  amount: number;
  currency: string;
  subtotal: number;
  tax: number;
  reverseCharge: boolean;
};

// Grund für die Messung, bewusst grob: war das Formular unvollständig (`validation`, kein
// Abbuchungsversuch), hat die Bank abgelehnt (`declined`) oder scheiterte die Freigabe per
// 3-D Secure (`authentication`). Der genaue Ablehnungsgrund der Bank (z. B. fehlende Deckung)
// wird NICHT gespeichert — er sagt etwas über den Menschen, nicht über den Funnel. Nie die
// Meldung: die kann Freitext enthalten.
const STRIPE_ERROR_TYPES = new Set([
  'api_error',
  'api_connection_error',
  'invalid_request_error',
  'rate_limit_error',
  'idempotency_error',
]);
function paymentFailureKind(err: { decline_code?: string; code?: string; type?: string }): string {
  if (err.type === 'validation_error') return 'validation';
  if (err.code === 'payment_intent_authentication_failure') return 'authentication';
  if (err.decline_code || err.code === 'card_declined') return 'declined';
  if (err.type === 'card_error') return 'card_error';
  return err.type && STRIPE_ERROR_TYPES.has(err.type) ? err.type : 'unknown';
}

export default function PrismPaymentElement({
  prismRunId,
  leads = 50,
  onFallback,
}: {
  prismRunId?: string;
  /** Firmen, die der Kauf aus diesem Lauf liefert (dieselbe Zahl wie die Angebots-Texte). */
  leads?: number;
  /** Called when element mode is off (409) or init fails → caller shows the CTA. */
  onFallback: () => void;
}) {
  const lang = useLang();
  // #14: alle 7 Funnel-Sprachen; unbekanntes lang → EN (nie Crash).
  const s = STR[lang as keyof typeof STR] ?? STR.en;
  const tx = TAX_STR[lang as keyof typeof TAX_STR] ?? TAX_STR.en;

  const [checking, setChecking] = useState(true);
  // LEA-430 #2: Rechnungsfelder überleben einen Refresh — Restore einmalig beim
  // Mount (Lazy-Initializer), danach spiegelt der Save-Effekt unten jede Änderung
  // nach sessionStorage. Der Mint feuert nach dem Restore automatisch neu (der
  // Tipp-Effekt sieht vollständiges Billing ohne clientSecret). Kartendaten leben
  // ausschließlich im Stripe-iframe und werden NIE persistiert.
  const [restored] = useState(loadBillingSnapshot);
  const [name, setName] = useState(restored?.name ?? '');
  const [country, setCountry] = useState(restored?.country || (LOCALE_COUNTRY[lang] ?? 'DE'));
  // Rechnungsadresse (Janik 11.07.): Straße/PLZ/Ort werden wieder in UNSEREM Formular
  // erfasst und VOR dem Mint an den Customer geschrieben, damit die 49-€-Rechnung eine
  // §14-konforme B2B-Empfängeradresse trägt (das Payment Element sammelt sie sonst nur
  // in die Zahlungsmethode → landet NICHT auf der Rechnung). Prefill der Element-Felder
  // via defaultValues → keine Doppel-Eingabe.
  const [street, setStreet] = useState(restored?.street ?? '');
  const [postal, setPostal] = useState(restored?.postal ?? '');
  const [city, setCity] = useState(restored?.city ?? '');
  const [state, setState] = useState(restored?.state ?? '');
  // Messung: erste Eingabe ins Rechnungsformular, einmal je Öffnen. Der nach einem Refresh
  // wiederhergestellte Stand zählt nicht als Eingabe (dann kann „Zahlungsfelder geladen“ ohne
  // „Eingabe begonnen“ vorkommen — je Konto und Lauf zählen).
  const formStartedRef = useRef(false);
  const walletSeenRef = useRef<Set<string>>(new Set());
  const billingNow = `${name}|${country}|${street}|${postal}|${city}|${state}`;
  const initialBillingRef = useRef(billingNow);
  useEffect(() => {
    if (formStartedRef.current) return;
    if (billingNow === initialBillingRef.current) return;
    formStartedRef.current = true;
    sendPrismEvent('prism_checkout_form_started', prismRunId);
  }, [billingNow, prismRunId]);
  // Bundesland/Provinz nur für Länder, deren Adresse es braucht (US/CA/AU/… + US-Tax).
  const stateRequired = STATE_COUNTRIES.has(country);
  // Rechnung braucht die volle Adresse → Mint erst wenn Name + Straße + PLZ + Ort (+ State,
  // wo nötig) da sind.
  const billingComplete = () =>
    name.trim().length >= 2 &&
    !!street.trim() &&
    !!postal.trim() &&
    !!city.trim() &&
    (!stateRequired || !!state.trim());
  // Länder-Optionen: DACH oben angepinnt, Rest alphabetisch nach in der UI-Sprache
  // lokalisiertem Namen (Intl.DisplayNames). Fällt bei fehlendem Intl-Support auf den
  // Code zurück. Memoisiert pro Sprache (Janik 11.07.).
  const countryOptions = useMemo(() => {
    let dn: Intl.DisplayNames | null = null;
    try {
      dn = new Intl.DisplayNames([lang], { type: 'region' });
    } catch {
      dn = null;
    }
    const label = (code: string) => dn?.of(code) ?? code;
    const top = PRIORITY_COUNTRIES.map((code) => ({ code, label: label(code) }));
    const rest = OTHER_COUNTRY_CODES.filter((c) => !PRIORITY_COUNTRIES.includes(c))
      .map((code) => ({ code, label: label(code) }))
      .sort((a, b) => a.label.localeCompare(b.label, lang));
    return [...top, ...rest];
  }, [lang]);
  // USt-IdNr.-Feld nur zeigen, wo ein VAT-ID-Typ unterstützt wird (EU + CH + GB); für
  // z. B. US/JP/NO gibt es hier kein Feld (und kein deutsches Beispiel-Format).
  const vatSupported = vatTypeForCountry(country) !== null;
  const [vatId, setVatId] = useState(restored?.vatId ?? '');
  // Restaurierte USt-IdNr. sichtbar machen: das <details> startet dann aufgeklappt.
  // Konstant über alle Renders → React fasst das open-Attribut nach dem Mount nie
  // wieder an, der User kann normal zu-/aufklappen.
  const vatDefaultOpen = !!restored?.vatId?.trim();
  const [busy, setBusy] = useState(false);
  const [reminting, setReminting] = useState(false);
  // Reaktives Gegenstück zu mintingRef (Ref rendert nicht): steuert, ob der Payment-
  // Bereich einen echten Lade-Spinner zeigt (Mint/Mount läuft) vs. den „Namen
  // eingeben"-Hinweis (Janik 10.07.: leerer Name → Element mintet nie → früher
  // Endlos-Spinner). Für Initial- UND Re-Mint gesetzt.
  const [minting, setMinting] = useState(false);
  const [elReady, setElReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped after every successful (re-)mint → drives the mount effect. Replaces the old
  // `step` trigger: the element (re-)mounts exactly when a new client secret exists, and
  // captures the then-current name+country as defaultValues (same "capture then-current"
  // pattern as before, hence the biome-ignore on the mount effect stays).
  const [secretVersion, setSecretVersion] = useState(0);
  // LEA-429: real invoice figures from the last (re-)mint → the summary shows charged==displayed
  // (DE B2C 58,31 €, EU-B2B reverse charge 49 €). null until the first mint; refreshed on re-mint.
  const [amountInfo, setAmountInfo] = useState<AmountInfo | null>(null);

  // Synchronous re-entrancy lock (state updates are async → a fast double-submit could
  // otherwise confirm twice before the disabled attr applies).
  const submittingRef = useRef(false);
  // Serializes mint()/re-mint() calls so a quick name-blur + country-change can't fire two
  // overlapping mints.
  const mintingRef = useRef(false);
  // Coalesce a tax-relevant change (Land/USt-IdNr.) that arrives WHILE a mint is in flight:
  // dropping it would leave the sub minted with a STALE country while the UI shows the new
  // one, and the user could pay against the wrong tax country (Risk f). Instead we remember
  // it and re-mint once the in-flight mint settles; the signature guard no-ops it if the
  // final selection already matches the minted sub.
  const pendingRemintRef = useRef(false);
  const clientSecretRef = useRef<string | null>(null);
  // Signature of the TAX-RELEVANT billing that minted the current sub. Mission #8: only
  // Land/USt-IdNr. trigger a re-mint (the name flows into the payload but the final billing
  // name comes from the Payment Element at confirm time). Keeps orphaned incomplete subs
  // minimal. Die verwaiste alte Sub bleibt (heute akzeptiertes Verhalten).
  const initSigRef = useRef<string | null>(null);
  const payHostRef = useRef<HTMLDivElement | null>(null);
  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const readyFiredRef = useRef(false);

  // LEA-430 #2: jede Feldänderung nach sessionStorage spiegeln, damit ein Refresh
  // mitten im Bezahlen Name/Adresse/USt-IdNr. nicht verliert.
  useEffect(() => {
    saveBillingSnapshot({ name, country, street, postal, city, state, vatId });
  }, [name, country, street, postal, city, state, vatId]);

  // Decide up front: if element mode is dark, fall back to the embedded/redirect CTA
  // immediately (never show the form for nothing). Runtime probe — flips per-env.
  // Messung (15.10.2026): Fällt die Probe zurück, sieht der Besucher statt des Formulars den
  // Weiterleitungs-Knopf, und „Eingabe begonnen“ kann gar nicht kommen. Das zählt jetzt als
  // prism_payment_failed mit Grund (init_http_409 = Element-Modus auf dem Server aus).
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once — onFallback identity must not re-probe.
  useEffect(() => {
    let cancelled = false;
    probePrismElementMode().then((probe) => {
      if (cancelled) return;
      if (probe.on) {
        setChecking(false);
        return;
      }
      sendPrismEvent('prism_payment_failed', prismRunId, undefined, probe.code);
      onFallback();
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-Mint-Signatur: Land + USt-IdNr. (tax-relevant) UND die Rechnungsadresse — ändert der
  // Kunde die Adresse NACH dem ersten Mint, wird mit der korrigierten Adresse neu gemintet,
  // damit die Rechnung sie trägt. Name bleibt draußen (tippt sich sonst re-mint-Spam).
  function billingSignature() {
    return JSON.stringify({
      country,
      vatId: vatId.trim(),
      street: street.trim(),
      postal: postal.trim(),
      city: city.trim(),
      state: stateRequired ? state.trim() : '',
    });
  }

  function buildBilling(): PrismElementBilling {
    const vatType = vatId.trim() ? vatTypeForCountry(country) : null;
    // §14: volle Rechnungsadresse an den Mint → landet auf dem Customer und damit auf der
    // 49-€-Rechnung (vorher nur { country } → Rechnung ohne Straße/PLZ/Ort).
    return {
      name: name.trim(),
      address: {
        country,
        line1: street.trim(),
        postal_code: postal.trim(),
        city: city.trim(),
        // State nur mitschicken, wo das Land es braucht (sonst würde ein stehengebliebener
        // US-State beim Wechsel auf z. B. DE fälschlich mitgehen).
        ...(stateRequired && state.trim() ? { state: state.trim() } : {}),
      },
      ...(vatType ? { vatId: { type: vatType, value: vatId.trim() } } : {}),
    };
  }

  // Single mint path with two entry semantics:
  //  - remint=false → INITIAL mint on the first name blur. Backend requires a non-empty
  //    name, so we canNOT mint on overlay open; blur (guard >= 2 chars) is the natural
  //    debounce the old 6 required fields + explicit submit used to provide → one orphan
  //    sub at most, not one per keystroke (Risk 2: sub-spam).
  //  - remint=true → Land-/USt-IdNr.-Änderung nach dem ersten Mint. Signaturvergleich
  //    (nur tax-relevante Felder) → neuer Mint nur bei echter Änderung. Neues clientSecret,
  //    secretVersion++ → Mount-Effect unmountet altes Element und mountet neues.
  async function mint(remint: boolean) {
    if (mintingRef.current) {
      // A remint requested mid-flight must not be lost — reconcile after the current mint.
      if (remint) pendingRemintRef.current = true;
      return;
    }
    const sig = billingSignature();
    if (remint) {
      // Re-Mint nur mit vollständiger Adresse — sonst entstünde beim Länderwechsel (z. B.
      // DE→US, State noch leer) eine Zwischen-Rechnung mit unvollständiger Adresse.
      if (!clientSecretRef.current || initSigRef.current === sig || !billingComplete()) return;
    } else {
      // Initial-Mint erst wenn die volle Rechnungsadresse steht (§14) — sonst entstünde
      // die 49-€-Rechnung mit unvollständiger Empfängeradresse.
      if (clientSecretRef.current || !billingComplete()) return;
    }
    mintingRef.current = true;
    setMinting(true);
    setError(null);
    if (remint) setReminting(true);
    try {
      const init = await startPrismTripwirePaymentElement(buildBilling(), prismRunId);
      if (!init.clientSecret || !init.publishableKey) throw new Error('no client secret');
      clientSecretRef.current = init.clientSecret;
      initSigRef.current = sig;
      stripeRef.current = await loadStripeCached(init.publishableKey);
      // LEA-429: capture the real amount+tax so the summary reflects the country/VAT-ID just
      // minted. Refreshed on every re-mint → the "Heute fällig" line updates when the buyer
      // changes country or adds a VAT-ID (reverse charge → back to €49).
      // Guard the deploy window: web + API are independent DO apps, so a new web bundle can
      // briefly run against a pre-LEA-429 API with no amount field → undefined → "NaN €".
      // Only trust finite figures; otherwise leave amountInfo null → the €49 + netVat pre-state.
      if (Number.isFinite(init.amount) && Number.isFinite(init.subtotal)) {
        setAmountInfo({
          amount: init.amount,
          currency: init.currency,
          subtotal: init.subtotal,
          tax: init.tax,
          reverseCharge: init.reverseCharge,
        });
      }
      setElReady(false);
      setSecretVersion((v) => v + 1);
    } catch (err) {
      // 409 = element mode dark; ANY other hard init failure = degrade to the proven
      // embedded/redirect Checkout rather than dead-ending the funnel.
      if (err instanceof BillingError && err.status === 409) {
        onFallback();
        return;
      }
      console.error('[prism] payment-element init failed → fallback:', err);
      // Antwort vom Server (init_http_<Status>) getrennt von „keine Antwort“ (init_network):
      // Letzteres entsteht auch, wenn der Besucher mitten im Laden den Tab schließt.
      sendPrismEvent(
        'prism_payment_failed',
        prismRunId,
        undefined,
        err instanceof BillingError ? `init_http_${err.status}` : 'init_network',
      );
      onFallback();
    } finally {
      mintingRef.current = false;
      setMinting(false);
      setReminting(false);
      // A tax-relevant change landed during this mint → re-mint with the now-current
      // signature (no-op via the guard if it already matches the minted sub). On a failed
      // initial mint there is no clientSecret yet, so this safely no-ops after onFallback().
      if (pendingRemintRef.current) {
        pendingRemintRef.current = false;
        void mint(true);
      }
    }
  }

  // Land-Re-Mint auf Änderung: der onChange setzt den State, dieser Effect liest den
  // frischen Wert (ein direkter mint() im Handler läse noch das alte country). Beim ersten
  // Render ohne Sub → Guard in mint() greift (kein clientSecret) → No-op.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-mint only on tax-relevant country change; mint reads then-current state.
  useEffect(() => {
    void mint(true);
  }, [country]);

  // Om-QA 11.07. — das Element folgt jetzt dem Namen:
  //  #2 Beim TIPPEN laden (debounced), nicht erst bei Blur — vorher tat sich nach
  //     dem Namen sichtbar nichts, bis man wegklickte („fühlt sich kaputt an"). Der
  //     mint() setzt minting=true → der #506-Spinner zeigt jetzt das Laden an.
  //  #1 Beim LÖSCHEN des Namens (< 2 Zeichen) das Element wieder abbauen und zum
  //     Hinweis zurück (vorher blieb das Kartenfeld stehen). secretVersion→0 lässt
  //     den Mount-Effect das Element unmounten und die Overlay-Logik den Hinweis zeigen.
  //  Guard in mint() erzwingt EINEN Sub; ein verworfener Zwischen-Sub verhält sich wie
  //  beim Land-Re-Mint (akzeptierte incomplete-Orphans, Stripe verwirft sie).
  // biome-ignore lint/correctness/useExhaustiveDependencies: debounced trigger on name; mint reads then-current state.
  useEffect(() => {
    // Review LEA-430: NICHT minten, solange die Element-Mode-Probe (checking)
    // noch läuft — dann ist das payHostRef-Div nicht gerendert (die Komponente
    // zeigt bis dahin einen Spinner). Ein Mint in diesem Fenster setzt zwar das
    // clientSecret + secretVersion, aber der Mount-Effekt findet keinen Host und
    // re-runnt später nicht mehr (Deps = [secretVersion]) → Dauer-Spinner, Pay
    // deaktiviert. Bei restaurierten Rechnungsfeldern ist billingComplete() sofort
    // wahr, also rennt der 650ms-Timer gegen die Netz-Probe — genau dieses Fenster.
    // `checking` in den Deps: sobald die Probe auflöst, feuert der Effekt erneut.
    if (checking) return;
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      if (clientSecretRef.current) {
        clientSecretRef.current = null;
        initSigRef.current = null;
        setElReady(false);
        setSecretVersion(0);
        // Merge-Nachzug (#508 ∩ LEA-429): auch den geminteten Betrag zurücksetzen,
        // sonst zeigt die Summary den echten Betrag (z.B. 58,31 €) neben dem
        // „Namen eingeben"-Hinweis → zurück auf den 49-€-Vorzustand mit „zzgl. USt.".
        setAmountInfo(null);
      }
      return;
    }
    if (clientSecretRef.current) return; // bereits gemintet → nichts zu tun
    if (!billingComplete()) return; // §14: Mint/Element erst mit voller Rechnungsadresse
    const t = setTimeout(() => void mint(false), 650);
    return () => clearTimeout(t);
  }, [name, street, postal, city, state, checking]);

  // Mount the Payment Element whenever a new client secret exists (secretVersion bump).
  // Captures the then-current name+country as defaultValues → must NOT re-mount per keystroke.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional single (re-)mount, see above.
  useEffect(() => {
    if (!clientSecretRef.current || !payHostRef.current) return;
    let cancelled = false;
    let mounted: StripePaymentElement | null = null;
    (async () => {
      try {
        const stripe = stripeRef.current;
        if (!stripe || cancelled) return;
        const elements = stripe.elements({
          clientSecret: clientSecretRef.current ?? undefined,
          appearance: { theme: 'stripe', variables: { borderRadius: '10px' } },
        });
        // LEA-419 prod incident (Om, real card): passing billing via
        // confirmParams.payment_method_data while Elements is created WITH a clientSecret
        // broke inline 3-D Secure — confirmPayment hung and the sub stayed `incomplete`.
        // Let the Payment Element collect billing itself again (the flow that passed 3-DS
        // on staging), but PRE-FILL name+country from step-1 so the country still defaults
        // correctly (the original India-default fix) instead of IP-guessing.
        const paymentEl = elements.create('payment', {
          layout: 'tabs',
          // Apple Pay / Google Pay (16.10.2026): beide laufen über 'card' (payment_settings der
          // Subscription erlaubt card). 'auto' = zeigen, wo Gerät + Browser es können. Apple Pay
          // erscheint erst, wenn die Domain in Stripe registriert ist (Anleitung im PR).
          wallets: { applePay: 'auto', googlePay: 'auto' },
          defaultValues: {
            billingDetails: {
              name: name.trim(),
              // Prefill der in unserem Formular erfassten Rechnungsadresse → das Element zeigt
              // sie vorbefüllt (keine Doppel-Eingabe). 3DS unberührt (kein fields:'never',
              // kein payment_method_data — nur defaultValues, s. #498).
              address: {
                country,
                line1: street.trim(),
                postal_code: postal.trim(),
                city: city.trim(),
                ...(stateRequired && state.trim() ? { state: state.trim() } : {}),
              },
            },
          },
        });
        if (cancelled || !payHostRef.current) return;
        paymentEl.on('ready', () => {
          setElReady(true);
          if (!readyFiredRef.current) {
            readyFiredRef.current = true;
            pushDataLayer('prism_checkout_ready', {});
            sendPrismEvent('prism_payment_ready', prismRunId);
          }
        });
        // Messung: Wallet gewählt (einmal je Art und Öffnen). value.type ist 'apple_pay' /
        // 'google_pay', sobald der Kunde den Wallet-Reiter wählt.
        paymentEl.on('change', (ev) => {
          const type = (ev as { value?: { type?: string } }).value?.type;
          if ((type === 'apple_pay' || type === 'google_pay') && !walletSeenRef.current.has(type)) {
            walletSeenRef.current.add(type);
            pushDataLayer('prism_wallet_selected', { wallet: type });
            sendPrismEvent('prism_wallet_selected', prismRunId, undefined, undefined, type);
          }
        });
        paymentEl.mount(payHostRef.current);
        mounted = paymentEl;
        elementsRef.current = elements;
      } catch {
        if (!cancelled) setError(s.errField);
      }
    })();
    return () => {
      cancelled = true;
      mounted?.unmount();
    };
  }, [secretVersion, s.errField]);

  async function submitPay() {
    if (submittingRef.current) return;
    const stripe = stripeRef.current;
    const elements = elementsRef.current;
    if (!stripe || !elements) return;
    submittingRef.current = true;
    setError(null);
    setBusy(true);
    pushDataLayer('prism_payment_submitted', {});
    sendPrismEvent('prism_payment_submitted', prismRunId);
    const { error: confirmErr } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/billing/success` },
    });
    // If we're here, confirmation failed (success redirects away). Surface the message.
    if (confirmErr) {
      setError(confirmErr.message ?? s.errPay);
      sendPrismEvent('prism_payment_failed', prismRunId, undefined, paymentFailureKind(confirmErr));
    }
    submittingRef.current = false;
    setBusy(false);
  }

  if (checking) {
    return (
      <div className="flex items-center justify-center py-6 text-ink-muted">
        <svg
          aria-hidden="true"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          style={{ animation: 'lea-spin .85s linear infinite' }}
        >
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
      </div>
    );
  }

  // LEA-429: "Heute fällig" = the REAL charged amount + a tax note. Before the first mint
  // (no name yet) show net €49 + a generic "zzgl. USt." hint; once minted, the invoice's true
  // total with breakdown — DE B2C "58,31 € · 49 € + 19% USt.", EU-B2B "49,00 € · Reverse Charge".
  const cur = (amountInfo?.currency ?? 'eur').toUpperCase();
  const dueAmount = amountInfo ? fmtMoney(amountInfo.amount, lang, cur) : fmtMoney(4900, lang);
  // Janik 11.07.: KEIN generischer „zzgl. USt."-Hinweis mehr im Pre-Mint/Haken-Zustand →
  // der Kunde sieht zunächst nur „49 €". Der ECHTE Endbetrag (inkl. Steuer bzw. Reverse
  // Charge) erscheint weiterhin, sobald Land/Name eingegeben sind und gemintet wurde
  // (amountInfo-Zweige unten) — „der finale Betrag, wenn der Kunde zahlt".
  let taxNote = '';
  if (amountInfo) {
    if (amountInfo.reverseCharge) {
      taxNote = REVERSE_CHARGE_LABEL;
    } else if (amountInfo.tax > 0 && amountInfo.subtotal > 0) {
      const rate = Math.round((amountInfo.tax / amountInfo.subtotal) * 100);
      taxNote = `${fmtMoney(amountInfo.subtotal, lang, cur)} + ${rate}% ${tx.ust}`;
    }
  }

  return (
    <div className="pce space-y-3 text-left">
      {/* OUR honest order summary — the whole point of Stufe 4. */}
      <div className="rounded-xl border border-line bg-surface px-4 py-3.5 mb-1 text-left">
        <div className="text-[13px] font-semibold text-ink">LeadScraper Starter</div>
        <div className="mt-0.5 text-[13px] text-ink-muted leading-relaxed">
          {t('prism.abo.line')}
        </div>
        <div className="mt-3 pt-3 border-t border-line flex items-center justify-between gap-2">
          <span className="text-[13px] text-ink-muted">{s.dueToday}</span>
          {/* LEA-429: dynamic — never hardcode the number again. */}
          <span className="text-right">
            <span className="block text-[15px] font-semibold text-ink whitespace-nowrap">
              {dueAmount}
            </span>
            {taxNote && (
              <span className="block text-[11px] text-ink-soft whitespace-nowrap">{taxNote}</span>
            )}
          </span>
        </div>
      </div>

      {/* Name/Firma + Land nebeneinander ab sm; darunter das Payment Element (sammelt
          PLZ/Stadt/Straße selbst). EINE Fläche, kein Zwischenklick. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[12px] font-medium text-ink-muted mb-1" htmlFor="pce-name">
            {s.name}
          </label>
          <input
            id="pce-name"
            type="text"
            required
            autoComplete="name"
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            onBlur={() => void mint(false)}
            className="w-full rounded-lg border border-line px-3 py-2 text-[16px] sm:text-[14px] outline-none focus:border-brand-500"
          />
        </div>
        <div>
          <label
            className="block text-[12px] font-medium text-ink-muted mb-1"
            htmlFor="pce-country"
          >
            {s.country}
          </label>
          <select
            id="pce-country"
            value={country}
            onChange={(ev) => setCountry(ev.target.value)}
            className="w-full rounded-lg border border-line px-3 py-2 text-[16px] sm:text-[14px] outline-none focus:border-brand-500 bg-white"
          >
            {countryOptions.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Rechnungsadresse (§14, Janik 11.07.): Straße volle Breite, PLZ + Ort nebeneinander.
          Wird VOR dem Mint an den Customer geschrieben (→ vollständige Rechnung); das Payment
          Element ist damit vorbefüllt. onBlur-Re-Mint nur wenn die Adresse komplett ist. */}
      <div className="mt-3">
        <div className="text-[11px] font-semibold text-ink-soft uppercase tracking-wide mb-1.5">
          {s.billing}
        </div>
        <div className="mb-3">
          <label className="block text-[12px] font-medium text-ink-muted mb-1" htmlFor="pce-street">
            {s.street}
          </label>
          <input
            id="pce-street"
            type="text"
            required
            autoComplete="street-address"
            value={street}
            onChange={(ev) => setStreet(ev.target.value)}
            onBlur={() => {
              if (billingComplete()) void mint(true);
            }}
            className="w-full rounded-lg border border-line px-3 py-2 text-[16px] sm:text-[14px] outline-none focus:border-brand-500"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label
              className="block text-[12px] font-medium text-ink-muted mb-1"
              htmlFor="pce-postal"
            >
              {s.postal}
            </label>
            <input
              id="pce-postal"
              type="text"
              required
              autoComplete="postal-code"
              value={postal}
              onChange={(ev) => setPostal(ev.target.value)}
              onBlur={() => {
                if (billingComplete()) void mint(true);
              }}
              className="w-full rounded-lg border border-line px-3 py-2 text-[16px] sm:text-[14px] outline-none focus:border-brand-500"
            />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-ink-muted mb-1" htmlFor="pce-city">
              {s.city}
            </label>
            <input
              id="pce-city"
              type="text"
              required
              autoComplete="address-level2"
              value={city}
              onChange={(ev) => setCity(ev.target.value)}
              onBlur={() => {
                if (billingComplete()) void mint(true);
              }}
              className="w-full rounded-lg border border-line px-3 py-2 text-[16px] sm:text-[14px] outline-none focus:border-brand-500"
            />
          </div>
        </div>
        {stateRequired && (
          <div className="mt-3">
            <label
              className="block text-[12px] font-medium text-ink-muted mb-1"
              htmlFor="pce-state"
            >
              {s.state}
            </label>
            <input
              id="pce-state"
              type="text"
              required
              autoComplete="address-level1"
              value={state}
              onChange={(ev) => setState(ev.target.value)}
              onBlur={() => {
                if (billingComplete()) void mint(true);
              }}
              className="w-full rounded-lg border border-line px-3 py-2 text-[16px] sm:text-[14px] outline-none focus:border-brand-500"
            />
          </div>
        )}
      </div>

      {/* USt-IdNr. optional + einklappbar — faq-i-Muster aus PrismShell wiederverwendet.
          Nur für Länder mit unterstütztem VAT-ID-Typ (EU + CH + GB), Janik 11.07. */}
      {vatSupported && (
        <details className="faq-i" open={vatDefaultOpen || undefined}>
          <summary>
            {s.vatToggle}
            <svg
              className="chev"
              aria-hidden="true"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </summary>
          <div className="px-[15px] pb-[13px]">
            <label className="block text-[12px] font-medium text-ink-muted mb-1" htmlFor="pce-vat">
              {s.vat} <span className="text-ink-soft">{s.vatOpt}</span>
            </label>
            <input
              id="pce-vat"
              type="text"
              value={vatId}
              onChange={(ev) => setVatId(ev.target.value)}
              onBlur={() => void mint(true)}
              placeholder={VAT_PLACEHOLDER[country] ?? s.vatPlaceholder}
              className="w-full rounded-lg border border-line px-3 py-2 text-[16px] sm:text-[14px] outline-none focus:border-brand-500"
            />
          </div>
        </details>
      )}

      {/* Payment Element mit Overlay. Janik 10.07.: der Spinner lief ewig, solange
          kein Name da war (Element mintet erst ab Name → ohne Name nie ready). Jetzt
          zeigt der Bereich den Spinner NUR bei echtem Laden (Mint/Re-Mint in flight
          ODER Secret da & Element mountet gerade); ohne Secht → freundlicher Hinweis
          „Namen eingeben" statt Dauer-Spinner. */}
      <div className="relative min-h-[220px]">
        <div ref={payHostRef} />
        {minting || reminting || (secretVersion > 0 && !elReady) ? (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-lg animate-pulse"
            style={{ background: 'rgba(255,255,255,0.72)' }}
          >
            <svg
              aria-hidden="true"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              className="text-ink-muted"
              style={{ animation: 'lea-spin .85s linear infinite' }}
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
          </div>
        ) : !elReady ? (
          <div className="absolute inset-0 flex items-center justify-center rounded-lg px-6 text-center text-[13px] text-ink-muted">
            {s.enterName}
          </div>
        ) : null}
      </div>

      {error && <div className="text-[13px] text-red-600">{error}</div>}

      {/* EIN CTA. disabled bis Client-Secret da UND Element interaktionsbereit — killt den
          stillen submitPay-return bei elementsRef===null (Risk 4). */}
      <button
        type="button"
        onClick={submitPay}
        disabled={busy || reminting || !clientSecretRef.current || !elReady}
        className="co-pay w-full whitespace-nowrap disabled:opacity-60"
      >
        {busy ? s.paying : s.pay.replace('{n}', String(leads))}
      </button>
    </div>
  );
}
